/**
 * Order material: everything a sales order needs (furniture jobs' material plans, mattress lines from their BOM, the
 * store's special requests) and where each item stands — requisitioned, ordered, at the gate, received, held, issued.
 * Requisitions against an order are made in any slices (jobs, department, items, times); the system tracks quantities.
 */
import { z } from 'zod';
import type { DocStatus } from './documents';

const uuidOpt = z.string().uuid().nullable().optional().transform((v) => v ?? null);
const isoDate = z.string({ error: 'Enter a date' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date');
const optDate = z.union([z.literal(''), isoDate]).nullable().optional().transform((v) => v || null);
const opt = (max: number) => z.string().trim().max(max, `At most ${max} characters`).optional().default('');
const posQty = z.preprocess((v) => (v === '' || v == null ? undefined : v), z.coerce.number({ error: 'Enter the quantity' }).positive('Must be above 0').max(1e9));

/** Where an item of an order stands, earliest unfinished step first. */
export const MATERIAL_STAGES = ['NOT_REQUISITIONED', 'WAITING_APPROVAL', 'TO_ORDER', 'ON_ORDER', 'AT_GATE', 'IN_STORE', 'ISSUED'] as const;
export type MaterialStage = (typeof MATERIAL_STAGES)[number];
export const MATERIAL_STAGE_LABELS: Record<MaterialStage, string> = {
  NOT_REQUISITIONED: 'Not requisitioned',
  WAITING_APPROVAL: 'Waiting approval',
  TO_ORDER: 'To order',
  ON_ORDER: 'On order',
  AT_GATE: 'At gate',
  IN_STORE: 'In store',
  ISSUED: 'Issued',
};
export const MATERIAL_STAGE_TONE: Record<MaterialStage, 'neutral' | 'primary' | 'warning' | 'success' | 'danger'> = {
  NOT_REQUISITIONED: 'danger',
  WAITING_APPROVAL: 'warning',
  TO_ORDER: 'warning',
  ON_ORDER: 'primary',
  AT_GATE: 'primary',
  IN_STORE: 'success',
  ISSUED: 'neutral',
};

export interface DocRef { id: string; number: string | null; status: DocStatus; docType: string; date: string; qty: number }

/** One item of an order for one job (furniture) / mattress line / special request, with every step. */
export interface OrderMaterialRow {
  key: string;
  /** decimals: of the item's unit (0 = whole units); inactive: switched off (no new requisitions). */
  item: { id: string; code: string; name: string; uom: string; category: string; decimals: number; inactive: boolean };
  job: { id: string; number: string | null } | null;
  /** Mattress line of the order: size and quantity. */
  soLine: { id: string; label: string } | null;
  department: { id: string; name: string } | null;
  /** From a special request note (not from the production plan). */
  special: boolean;
  neededBy: string | null;
  required: number;
  /** Planned on requisitions (covered by buying or by stock). */
  planned: number;
  /** Still to requisition. */
  toRequisition: number;
  /** To buy on requisitions: waiting approval / approved. */
  requested: number;
  approved: number;
  overridden: boolean;
  ordered: number;
  atGate: number;
  received: number;
  held: number;
  issued: number;
  /** Still to issue. */
  remaining: number;
  stage: MaterialStage;
  late: boolean;
  /** Documents behind the numbers (click-through). */
  docs: DocRef[];
}

export interface OrderMaterial {
  order: { id: string; number: string | null; customer: string | null; date: string; committedDate: string | null };
  jobs: { id: string; number: string | null; stage: string; start: string | null }[];
  departments: { id: string; name: string }[];
  rows: OrderMaterialRow[];
  counts: Record<MaterialStage, number> & { items: number; late: number };
  /** Mattress sizes of the order without an approved BOM (their material cannot be worked out). */
  missingBom: string[];
}

/** Orders with material to follow (search / pick). */
export interface OrderPickRow {
  id: string;
  number: string | null;
  customer: string | null;
  date: string;
  committedDate: string | null;
  jobs: number;
  mattressLines: number;
  items: number;
  notRequisitioned: number;
  late: number;
}

/** Requisition against an order: one row per item for the part of the order chosen. */
export interface DemandRow {
  /** decimals: of the item's own unit (0 = whole units: PCS, SHEET…). */
  item: { id: string; code: string; name: string; uom: string; decimals: number; purchase: { uom: string; factor: number } | null };
  /** Switched off: it cannot be requisitioned (left out unless someone activates or replaces it). */
  inactive: { since: string | null; by: string | null } | null;
  /** The system rounded the quantity to buy up to whole units (from → demand). */
  roundedFrom: number | null;
  neededBy: string | null;
  required: number;
  /** Already planned on other requisitions. */
  planned: number;
  issued: number;
  /** Still to requisition (the need this requisition plans). */
  toRequisition: number;
  onHand: number;
  /** Stock not held for or promised to other orders. */
  freeStock: number;
  /** What the system says to buy: still to requisition less free stock, in whole purchase units. */
  demand: number;
  jobs: string[];
  departments: string[];
  /** Special request note lines not yet requisitioned (highlighted, ticked in by the user). */
  special: { srnLineId: string; number: string | null; qty: number; description: string }[];
}

/** Where an item is planned or in use (shown before switching it off, and on the planning check). */
/** Rounds up to the decimals a unit allows (0.5 PCS → 1): what can actually be bought or issued. */
export const roundUpTo = (qty: number, decimals: number) => {
  const f = 10 ** decimals;
  return Math.ceil(Math.round(qty * 10000) / 10000 * f - 1e-6) / f;
};

export interface ItemUsage {
  item: { id: string; code: string; name: string; status: string };
  boms: { id: string; product: string; revision: number; status: string }[];
  jobs: { id: string; number: string | null; order: string | null; qty: number }[];
  /** Orders whose material still needs it (not yet fully requisitioned). */
  orders: { id: string; number: string | null; customer: string | null }[];
  openRequisitions: { id: string; number: string | null; status: string }[];
  openPurchaseOrders: { id: string; number: string | null; status: string }[];
  heldForOrders: number;
  onHand: number;
}

/** Replace an item in planning: this order's jobs, and new draft revisions of the mattress BOMs that use it. */
export const replaceItemSchema = z.object({
  fromItemId: z.string().uuid(),
  toItemId: z.string({ error: 'Choose the item to use instead' }).uuid('Choose the item to use instead'),
  /** order: this order's jobs (and BOM drafts for its mattresses); everywhere: every open job and every BOM using it. */
  scope: z.enum(['order', 'everywhere']).default('order'),
  orderId: z.string().uuid().nullable().default(null),
});
export type ReplaceItemInput = z.infer<typeof replaceItemSchema>;
export interface ReplaceItemResult { jobs: number; bomDrafts: { id: string; product: string; revision: number }[] }

/** Planning check: BOMs and open jobs that use items no longer active (or merged into another). */
export interface PlanningCheckRow {
  item: { id: string; code: string; name: string; status: string; mergedInto: { id: string; code: string; name: string } | null };
  boms: { id: string; product: string; revision: number }[];
  jobs: number;
  orders: number;
}

// ---------- Approvals (Purchase & Inventory) ----------

/**
 * Approvals tabs (owner, 2026-11-02): pending for approval (first time), rectified (corrected and sent again), sent for
 * rectification, approved, all; purchase orders also changes after approval. "waiting" = what I may approve now.
 */
export const INV_APPROVAL_CARDS = ['waiting', 'pending', 'resubmitted', 'sent_back', 'changes', 'drafts', 'approved', 'cancelled', 'all'] as const;
export type InvApprovalCard = (typeof INV_APPROVAL_CARDS)[number];

/** Approvals by document: one card each, store documents together. */
export const INV_APPROVAL_GROUPS = [
  { key: 'requisitions', label: 'Requisitions', short: 'Req', types: ['inv.purchase_requisition'] },
  { key: 'orders', label: 'Purchase Orders', short: 'PO', types: ['inv.purchase_order'] },
  { key: 'igp', label: 'Inward Gate Passes', short: 'IGP', types: ['inv.gate_pass'] },
  { key: 'grn', label: 'Goods Receipts', short: 'GRN', types: ['inv.goods_receipt'] },
  { key: 'srn', label: 'Special Request Notes', short: 'SRN', types: ['inv.special_request'] },
  { key: 'store', label: 'Store documents', short: 'Store', types: ['inv.stock_issue', 'inv.stock_return', 'inv.purchase_return', 'inv.out_gate_pass', 'inv.stock_adjustment', 'inv.stock_transfer', 'inv.item_conversion', 'inv.opening_stock'] },
] as const;
export type InvApprovalGroup = (typeof INV_APPROVAL_GROUPS)[number]['key'];
export interface InvApprovalGroupCount { key: InvApprovalGroup; label: string; short: string; waiting: number; pending: number; overdue: number; sentBack: number; resubmitted: number; changes: number }
export interface InvApprovalRow {
  id: string;
  docType: string;
  typeLabel: string;
  screen: string;
  number: string | null;
  docDate: string;
  status: DocStatus;
  /** Vendor, or the order and customer, or the department / purpose. */
  party: string | null;
  order: { id: string; number: string | null; customer: string | null } | null;
  amount: number | null;
  lines: number;
  createdBy: string | null;
  submittedAt: string | null;
  waitingHours: number | null;
  overdue: boolean;
  /** I can approve it (right, not my own). */
  canApprove: boolean;
  version: number;
  /** Why it was sent back last (sent back / resubmitted). */
  sentBackReason: string | null;
  /** Resubmitted after a send-back: what changed (line by line). */
  changes: string[];
  /** What the approver should look at: changed demand, rate above last time, received over the PO, extra issue… */
  flags: { text: string; tone: 'warning' | 'danger' | 'neutral' }[];
  /** The document's own facts for its card (PO: requisition, VAT, delivery; IGP: PO, vehicle; GRN: PO, IGP…). */
  details: { label: string; value: string; tone?: 'danger' | 'warning' }[];
  /** Sub PO: its PO and why more is bought. */
  subPo?: { parent: string | null; reason: string } | null;
  /** One column each (owner, 2026-11-02): the document's type, the vendor / customer / department, who approved and when. */
  kindLabel: string;
  partyLabel: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  /** Pending change to an approved PO. */
  change?: { id: string; reason: string; summary: string; requestedBy: string | null; requestedAt: string } | null;
}

// ---------- Special request note (store) ----------

export const specialRequestSchema = z.object({
  docDate: isoDate,
  orderId: z.string({ error: 'Choose the order' }).uuid('Choose the order'),
  jobId: uuidOpt,
  departmentId: uuidOpt,
  reason: opt(300),
  notes: opt(500),
  lines: z.array(z.object({ itemId: z.string({ error: 'Select an item' }).uuid('Select an item'), qty: posQty, description: opt(300) })).min(1, 'Add at least one item').max(200),
});
export type SpecialRequestInput = z.infer<typeof specialRequestSchema>;

// ---------- Outward gate pass ----------

export const OGP_PURPOSES = ['RETURN_TO_VENDOR', 'REPAIR', 'OUTSIDE_WORK', 'OTHER'] as const;
export const OGP_PURPOSE_LABELS: Record<(typeof OGP_PURPOSES)[number], string> = { RETURN_TO_VENDOR: 'Return to vendor', REPAIR: 'Sent for repair', OUTSIDE_WORK: 'Outside work', OTHER: 'Other' };
export const outGatePassSchema = z
  .object({
    docDate: isoDate,
    purpose: z.enum(OGP_PURPOSES),
    supplierId: uuidOpt,
    partyName: opt(150),
    vehicleNo: opt(30),
    driverName: opt(80),
    /** Comes back (repair, outside work): by when. */
    returnable: z.boolean().default(false),
    expectedReturn: optDate,
    purchaseReturnId: uuidOpt,
    notes: opt(500),
    lines: z.array(z.object({ itemId: uuidOpt, description: opt(300), qty: posQty })).min(1, 'Add at least one item').max(200),
  })
  .refine((v) => !!v.supplierId || v.partyName.length > 0, { path: ['partyName'], message: 'Choose the vendor or write who takes it' })
  .refine((v) => !v.returnable || !!v.expectedReturn, { path: ['expectedReturn'], message: 'When does it come back?' })
  .refine((v) => v.lines.every((l) => !!l.itemId || l.description.length > 0), { path: ['lines'], message: 'Each line needs an item or a description' });
export type OutGatePassInput = z.infer<typeof outGatePassSchema>;

// ---------- Shelves ----------

export const shelfSchema = z.object({
  warehouseId: z.string({ error: 'Select the warehouse' }).uuid('Select the warehouse'),
  code: z.string().trim().min(1, 'Enter the code').max(20).transform((v) => v.toUpperCase()),
  name: opt(80),
  isActive: z.boolean().default(true),
});
export type ShelfInput = z.infer<typeof shelfSchema>;
export interface ShelfRow { id: string; warehouse: { id: string; code: string; name: string }; code: string; name: string; isActive: boolean; items: number }

// ---------- Cut-over ----------

export interface CutoverSummary { old: { docType: 'inv.purchase_requisition' | 'inv.purchase_order'; open: number; olderThan90: number }[] }
export const cutoverCloseSchema = z.object({
  docType: z.enum(['inv.purchase_requisition', 'inv.purchase_order']),
  before: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional().transform((v) => v ?? null),
  reason: z.string().trim().min(3, 'Give the reason').max(300),
});

/**
 * Purchase & Inventory dashboard, the store's control room (owner, 2026-11-03: "100X better"): where work is stuck along
 * the chain, what is at the gate today, late orders, price rises and the latest activity.
 */
export interface InvControlRoom {
  asOf: string;
  /** Requisition → PO → gate → receipt → bill: documents waiting at each step. */
  pipeline: { key: 'approve' | 'order' | 'arrive' | 'gate' | 'bill'; label: string; count: number; late: number; screen: string; search?: Record<string, string> }[];
  /** Inward gate passes dated today. */
  gateToday: { id: string; number: string | null; vendor: string | null; po: string | null; status: DocStatus; time: string | null; lines: number }[];
  /** The next approved purchase orders due to arrive (today or later) with something still to come — what the gate should expect. */
  dueSoon: { id: string; number: string | null; vendor: string | null; deliveryDate: string; inDays: number; amount: number }[];
  /** Approved purchase orders past their delivery date with something still to come. */
  late: { id: string; number: string | null; vendor: string | null; deliveryDate: string; daysLate: number; amount: number }[];
  /** Purchase order lines (last 60 days) dearer than the last price paid for the item. */
  priceRises: { poId: string; po: string | null; vendor: string | null; itemCode: string; itemName: string; was: number; now: number; pct: number }[];
  /** The latest actions on purchase and store documents. */
  activity: { id: string; docId: string; screen: string; number: string | null; type: string; action: string; actor: string | null; at: string }[];
}

/**
 * Purchase order print (owner, 2026-11-03: "1000× better"). Two copies: for the supplier (never other vendors' names or
 * prices) and internal (each item's last and second-last rate and its recent purchases, rate up / down marked).
 */
export interface PoPrintData {
  id: string;
  copy: 'supplier' | 'internal';
  number: string | null;
  docDate: string;
  status: DocStatus;
  subPoOf: string | null;
  supplier: { code: string; name: string; address: string; trn: string; phone: string; email: string; contact: string | null; paymentTerms: string | null };
  order: { requisition: string | null; salesOrder: string | null; customer: string | null; jobs: string[]; deliveryDate: string | null; deliverTo: string | null; supplierRef: string | null; vatMode: 'EXCLUSIVE' | 'INCLUSIVE' | 'NONE' | null; vatRate: number };
  lines: {
    lineNo: number;
    code: string | null;
    name: string;
    description: string;
    uom: string;
    /** Purchase unit when it differs from the stock unit: qty in it, and stock units per purchase unit. */
    purchase: { uom: string; qty: number; factor: number } | null;
    qty: number;
    rate: number;
    net: number;
    vatPct: number;
    tax: number;
    total: number;
    note: string | null;
    /** Internal copy only: earlier purchases of the item, newest first (last = [0], second last = [1]). */
    history?: { date: string; po: string | null; vendor: string | null; qty: number; rate: number }[];
  }[];
  totals: { net: number; tax: number; total: number };
  notes: string;
  terms: string;
  sign: { preparedBy: string | null; preparedAt: string | null; checkedBy: string | null; checkedAt: string | null; approvedBy: string | null; approvedAt: string | null };
}

/** Signature blocks of a purchase / store document print, from its approval trail. */
export interface InvPrintTrail { preparedBy: string | null; preparedAt: string | null; checkedBy: string | null; checkedAt: string | null; approvedBy: string | null; approvedAt: string | null; postedBy: string | null; postedAt: string | null }
